import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES } from "@src/appV2/api";
import {
  type IncludedWorkerSchema,
  type IncludedWorkplaceSchema,
  useGetShifts,
} from "@src/appV2/Shifts/api/useGetShifts";
import { useMemo } from "react";

const SHIFT_ID_REGEX = /^[a-f0-9]{24}$/i;

export interface ShiftSearchResult {
  id: string;
  agentReq: string;
  start: string;
  end: string;
  deleted?: boolean;
  charge?: number;
  pay?: number;
  agent?: {
    name?: string;
    userId?: string;
  };
  facility?: {
    name: string;
    userId: string;
    tmz: string;
  };
}

interface UseSearchShiftParams {
  shiftId: string | null | undefined;
}

export function isValidShiftId(shiftId?: string | null): shiftId is string {
  if (!shiftId) {
    return false;
  }
  return SHIFT_ID_REGEX.test(shiftId.trim());
}

// Fields matching ShiftSearchPage pattern
const shiftFieldsToSelect = [
  "createdAt",
  "schedule.startAt",
  "schedule.endAt",
  "requirements.qualificationName",
  "pay.total",
] as const;

const workplaceFieldsToSelect = ["name", "timezone"] as const;
const workerFieldsToSelect = ["name"] as const;

export function useSearchShift(params: UseSearchShiftParams) {
  const { shiftId } = params;
  const isValid = isValidShiftId(shiftId);

  const query = useGetShifts(
    {
      filter: {
        id: isValid ? [shiftId] : undefined,
      },
      include: [API_TYPES.workplace, API_TYPES.worker],
      fields: {
        shift: [...shiftFieldsToSelect],
        workplace: [...workplaceFieldsToSelect],
        worker: [...workerFieldsToSelect],
      },
      page: { size: 1 },
    },
    {
      enabled: isValid,
      retry: false,
      staleTime: 60 * 1000,
    }
  );

  const transformedData = useMemo((): ShiftSearchResult | undefined => {
    const shift = query.data?.data[0];
    if (!shift) {
      return undefined;
    }

    const workplaceRelationId = shift.relationships?.workplace?.data?.id;
    const workerRelationId = shift.relationships?.worker?.data?.id;

    const workplace = query.data?.included?.find(
      (item): item is IncludedWorkplaceSchema =>
        item.type === API_TYPES.workplace && item.id === workplaceRelationId
    );

    const worker = query.data?.included?.find(
      (item): item is IncludedWorkerSchema =>
        item.type === API_TYPES.worker && item.id === workerRelationId
    );

    const startAt = shift.attributes?.schedule?.startAt;
    const endAt = shift.attributes?.schedule?.endAt;

    if (!isDefined(startAt) || !isDefined(endAt)) {
      return undefined;
    }

    return {
      id: shift.id,
      agentReq: shift.attributes?.requirements?.qualificationName ?? "Unknown",
      start: startAt,
      end: endAt,
      pay: shift.attributes?.pay?.total,
      facility: workplace
        ? {
            name: workplace.attributes?.name ?? "",
            userId: workplace.id,
            tmz: workplace.attributes?.timezone ?? "America/Los_Angeles",
          }
        : undefined,
      agent: worker
        ? {
            name: worker.attributes?.name,
            userId: worker.id,
          }
        : undefined,
    };
  }, [query.data]);

  return {
    data: transformedData,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
